import React, { useState, useEffect } from 'react';
import { useUploadManager } from '../../../contexts/UploadManagerContext';
import type { UploadJob, UploadJobStatus } from '../../../services/uploadManager';
import './UploadBar.css';
import './UploadBar.glass.css';

// ─── Status helpers ────────────────────────────────────────────

function statusIcon(status: UploadJobStatus, online: boolean): React.ReactNode {
  const icon = (() => {
    if (!online) {
      return (
        <svg className="ub-icon ub-icon--paused" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="1" y1="1" x2="23" y2="23" /><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" /><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" /><path d="M10.71 5.05A16 16 0 0 1 22.56 9" /><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      );
    }
    switch (status) {
      case 'compressing':
        return (
          <svg className="ub-icon ub-icon--spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
          </svg>
        );
      case 'uploading':
        return (
          <svg className="ub-icon ub-icon--pulse" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="17,1 21,5 17,9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7,23 3,19 7,15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
        );
      case 'finalizing':
        return (
          <svg className="ub-icon ub-icon--spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
          </svg>
        );
      case 'done':
        return (
          <svg className="ub-icon ub-icon--done" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="ub-icon ub-icon--failed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        );
      default: // queued
        return (
          <svg className="ub-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" />
          </svg>
        );
    }
  })();
  return <span key={status} className="ub-icon-wrap ub-icon-wrap--entering">{icon}</span>;
}

function statusLabel(job: UploadJob, online: boolean): string {
  if (!online && job.status !== 'done' && job.status !== 'failed') return 'Waiting for network...';
  switch (job.status) {
    case 'queued': return 'Queued';
    case 'compressing': return `Compressing... ${job.progress}%`;
    case 'uploading': return `Uploading... ${job.progress}%`;
    case 'finalizing': return 'Saving...';
    case 'done': return 'Done';
    case 'failed': return 'Failed';
  }
}

function barLabel(
  current: UploadJob | undefined,
  processingCount: number,
  failedCount: number,
  doneCount: number,
  online: boolean,
): string {
  if (!online && processingCount > 0) return 'Paused — offline';
  if (failedCount > 0 && processingCount === 0) return `${failedCount} upload${failedCount > 1 ? 's' : ''} failed`;
  if (!current) return doneCount > 0 ? 'Uploads complete' : '';
  const extra = processingCount > 1 ? ` (+${processingCount - 1} more)` : '';
  if (current.status === 'compressing') return `Compressing "${current.taskTitle}"${extra}`;
  if (current.status === 'uploading') return `Uploading "${current.taskTitle}"${extra}`;
  if (current.status === 'finalizing') return `Saving "${current.taskTitle}"${extra}`;
  if (current.status === 'queued') return `Waiting to upload "${current.taskTitle}"${extra}`;
  return current.taskTitle;
}

// ─── Sensory rotating messages for long uploads ───────────────
const UPLOAD_SETS = [
  ['Packing it up', 'Wrapping with care', 'Tuning in', 'Listening closely', 'Tracing the details', 'Tucking it safely', 'Almost there'],
  ['Focusing the view', 'Catching every nuance', 'Sensing the patterns', 'Piecing it together', 'One moment more'],
  ['Still with you', 'Refining the picture', 'Just about ready'],
];

const BarLabel: React.FC<{
  current: UploadJob | undefined;
  processingCount: number;
  failedCount: number;
  doneCount: number;
  online: boolean;
}> = ({ current, processingCount, failedCount, doneCount, online }) => {
  const isActive = !!current && online && (
    current.status === 'uploading'
    || current.status === 'compressing'
    || current.status === 'finalizing'
  );

  const [setIdx, setSetIdx] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const [whimsyOn, setWhimsyOn] = useState(false);
  const [visible, setVisible] = useState(true);

  // Reset + kick off whimsy mode a few seconds into an active upload
  useEffect(() => {
    if (!isActive) {
      setWhimsyOn(false);
      setSetIdx(0);
      setMsgIdx(0);
      setVisible(true);
      return;
    }
    const start = setTimeout(() => setWhimsyOn(true), 3000);
    return () => clearTimeout(start);
  }, [isActive]);

  // Rotate messages with a short opacity fade between them
  useEffect(() => {
    if (!isActive || !whimsyOn) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIdx(prev => {
          const currentSet = UPLOAD_SETS[setIdx];
          const next = prev + 1;
          if (next < currentSet.length) return next;
          if (setIdx < UPLOAD_SETS.length - 1) {
            setSetIdx(s => s + 1);
            return 0;
          }
          return 0; // loop final set
        });
        setVisible(true);
      }, 200);
    }, 2800);
    return () => clearInterval(id);
  }, [isActive, whimsyOn, setIdx]);

  const extra = processingCount > 1 ? ` (+${processingCount - 1} more)` : '';
  const label = whimsyOn && current
    ? `${UPLOAD_SETS[setIdx][msgIdx]}${extra}`
    : barLabel(current, processingCount, failedCount, doneCount, online);

  return (
    <span
      className="ub-bar-label"
      style={{ opacity: visible ? 0.9 : 0, transition: 'opacity 0.2s ease' }}
    >
      {label}
    </span>
  );
};

// ─── Job row (expanded view) ────────────────────────────────────

const JobRow: React.FC<{
  job: UploadJob;
  isOnline: boolean;
  onRetry: (id: number) => void;
  onCancel: (id: number) => void;
  onDismiss: (id: number) => void;
}> = ({ job, isOnline, onRetry, onCancel, onDismiss }) => (
  <div className={`ub-job ub-job--${job.status}`}>
    <div className="ub-job-left">
      {job.thumbnailUrl ? (
        <img className="ub-job-thumb" src={job.thumbnailUrl} alt="" />
      ) : (
        statusIcon(job.status, isOnline)
      )}
      <div className="ub-job-info">
        <span className="ub-job-title">{job.taskTitle}</span>
        <span className="ub-job-status">{statusLabel(job, isOnline)}</span>
        {job.status === 'failed' && job.error && (
          /* Diagnostic: surface the raw error — Firebase Storage messages
             embed the storage/* code — so the upload-failure root cause is
             visible on the live repro instead of just "Failed". */
          <span
            className="ub-job-status"
            style={{ opacity: 0.65, fontSize: '11px', wordBreak: 'break-word', marginTop: '2px' }}
          >
            {job.error}
          </span>
        )}
      </div>
    </div>
    <div className="ub-job-actions">
      {job.status === 'failed' && (
        <button className="ub-job-btn ub-job-btn--retry" onClick={() => onRetry(job.id)}>Retry</button>
      )}
      {['queued', 'compressing', 'uploading', 'finalizing'].includes(job.status) && (
        <button className="ub-job-btn ub-job-btn--cancel" onClick={() => onCancel(job.id)}>Cancel</button>
      )}
      {(job.status === 'done' || job.status === 'failed') && (
        <button className="ub-job-btn ub-job-btn--dismiss" onClick={() => onDismiss(job.id)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      )}
    </div>
    {(job.status === 'uploading' || job.status === 'compressing') && (
      <div className="ub-job-progress">
        <div className="ub-job-fill" style={{ width: `${job.progress}%` }} />
      </div>
    )}
  </div>
);

// ─── UploadBar ──────────────────────────────────────────────────

export const UploadBar: React.FC = () => {
  const { jobs, isOnline, retry, cancel, dismiss } = useUploadManager();
  const [expanded, setExpanded] = useState(false);

  // Auto-dismiss done jobs after 4 seconds
  useEffect(() => {
    const doneJobs = jobs.filter(j => j.status === 'done');
    if (doneJobs.length === 0) return;
    const timer = setTimeout(() => {
      doneJobs.forEach(j => dismiss(j.id));
    }, 4000);
    return () => clearTimeout(timer);
  }, [jobs, dismiss]);

  // Auto-collapse when all jobs are gone
  useEffect(() => {
    if (jobs.length === 0) setExpanded(false);
  }, [jobs.length]);

  if (jobs.length === 0) return null;

  const activeStatuses = ['queued', 'compressing', 'uploading', 'finalizing'] as const;
  const current = jobs.find(j => (activeStatuses as readonly string[]).includes(j.status))
    || jobs.find(j => j.status === 'failed')
    || jobs[0];

  const processingCount = jobs.filter(j => (activeStatuses as readonly string[]).includes(j.status)).length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;
  const doneCount = jobs.filter(j => j.status === 'done').length;

  // State tint modifier (glass theme only — see UploadBar.glass.css).
  // Failures take priority; otherwise "all done" once nothing is still
  // processing; else neutral.
  const stateMod =
    failedCount > 0 ? ' ub-wrapper--failed'
    : (processingCount === 0 && doneCount > 0) ? ' ub-wrapper--all-done'
    : '';

  return (
    <div className={`ub-wrapper${expanded ? ' ub-expanded' : ''}${stateMod}`}>
      {/* Collapsed bar */}
      <div className="ub-bar" onClick={() => setExpanded(!expanded)}>
        <div className="ub-bar-left">
          {statusIcon(current.status, isOnline)}
          <BarLabel
            current={current}
            processingCount={processingCount}
            failedCount={failedCount}
            doneCount={doneCount}
            online={isOnline}
          />
        </div>
        <div className="ub-bar-right">
          {(current.status === 'uploading' || current.status === 'compressing') && (
            <div className="ub-mini-progress">
              <div className="ub-mini-fill" style={{ width: `${current.progress}%` }} />
            </div>
          )}
          <svg
            className={`ub-chevron${expanded ? ' ub-chevron-up' : ''}`}
            width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </div>
      </div>

      {/* No browser uploads while the page is backgrounded — iOS suspends JS
          outright and this app registers no Background Sync — so this applies
          to every participant, not just the in-app-browser fallback. Shown
          whenever work is actually in flight and we're online (offline already
          says "Paused" in the bar label). */}
      {processingCount > 0 && isOnline && (
        <div className="ub-keepopen">Keep this screen open until uploading finishes</div>
      )}

      {/* Expanded job list */}
      {expanded && (
        <div className="ub-jobs">
          {jobs.map(job => (
            <JobRow
              key={job.id}
              job={job}
              isOnline={isOnline}
              onRetry={retry}
              onCancel={cancel}
              onDismiss={dismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
};
